import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import { isUserAdmin } from '../helpers/adminAuth';
import reportsService, { ApiAnalysisResponse, CompletenessAnalysis } from '../services/reportsService';
import './SpecAnalysisPage.css';

/**
 * Spec Analysis page with two-panel layout: spec viewer + findings
 */
export const SpecAnalysisPage: React.FC = () => {
  const { apiId } = useParams<{ apiId: string }>();
  const navigate = useNavigate();
  const { authState } = useOktaAuth();
  const isAdmin = isUserAdmin(authState);
  const [apiData, setApiData] = useState<ApiAnalysisResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analyzeLoading, setAnalyzeLoading] = useState(false);
  const [analyzeSuccess, setAnalyzeSuccess] = useState(false);
  const [selectedLine, setSelectedLine] = useState<number | null>(null);
  const lineRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  useEffect(() => {
    const fetchData = async () => {
      if (!apiId) {
        setError('API ID not provided');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const data = await reportsService.getAnalysis(authState, apiId);
        setApiData(data);
      } catch (err: any) {
        setError(err.message || 'Failed to load API analysis');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [apiId, authState]);

  const handleRunAnalysis = async () => {
    if (!apiId) return;

    setAnalyzeLoading(true);
    setAnalyzeSuccess(false);
    setError(null);

    try {
      await reportsService.analyzeSingleApi(authState, apiId, true);
      setAnalyzeSuccess(true);

      setTimeout(async () => {
        try {
          const data = await reportsService.getAnalysis(authState, apiId);
          setApiData(data);
          setAnalyzeSuccess(false);
        } catch (err) {
          console.error('Failed to refresh analysis:', err);
        }
      }, 1000);
    } catch (err: any) {
      setError(err.message || 'Failed to run analysis');
    } finally {
      setAnalyzeLoading(false);
    }
  };

  const jumpToLine = (line?: number) => {
    if (!line) return;
    setSelectedLine(line);
    lineRefs.current.get(line)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  if (loading) {
    return (
      <div className="spec-analysis-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading analysis...</p>
        </div>
      </div>
    );
  }

  if (error || !apiData) {
    return (
      <div className="spec-analysis-page">
        <div className="error-container">
          <span className="material-icons">error</span>
          <p>{error || 'Failed to load API analysis'}</p>
        </div>
      </div>
    );
  }

  // Use the API's own completeness data since we navigate to specific versions
  const analysis = apiData.apiCenterMetadata?.completeness;
  const lines = apiData.specText ? apiData.specText.split('\n') : [];

  // Build map of line numbers to violations
  const violationsByLine = new Map<number, Array<CompletenessAnalysis['findings'][0]>>();
  if (analysis?.findings) {
    for (const finding of analysis.findings) {
      if (finding.line) {
        if (!violationsByLine.has(finding.line)) {
          violationsByLine.set(finding.line, []);
        }
        violationsByLine.get(finding.line)!.push(finding);
      }
    }
  }

  // Get status badge
  const getStatusBadge = () => {
    if (!analysis) return null;
    const statusMap: { [key: string]: { className: string; label: string } } = {
      'compliant': { className: 'compliant', label: 'Compliant' },
      'partially-compliant': { className: 'partial', label: 'Partially Compliant' },
      'non-compliant': { className: 'non-compliant', label: 'Non-Compliant' },
    };
    const statusInfo = statusMap[analysis.status] || statusMap['partially-compliant'];
    return (
      <span className={`status-badge ${statusInfo.className}`}>
        {statusInfo.label}
      </span>
    );
  };

  return (
    <div className="spec-analysis-page">
      {/* Page Header */}
      <div className="page-header">
        <button className="back-button" onClick={() => navigate(-1)} title="Go back">
          ←
        </button>
        <div className="page-header-content">
          <div className="page-title-row">
            <h1>Spec Analysis: {apiData.fullApiName}</h1>
            {getStatusBadge()}
          </div>
        </div>
        {!apiData.isParentAggregate && (
          <button
            className={`run-analysis-button ${analyzeLoading ? 'loading' : ''}`}
            onClick={handleRunAnalysis}
            disabled={analyzeLoading || !isAdmin}
            title={!isAdmin ? 'Only admins can run analysis' : 'Run Analysis'}
          >
            {analyzeLoading ? 'Analyzing...' : 'Run Analysis'}
          </button>
        )}
      </div>

      {analyzeSuccess && (
        <div className="success-toast">
          ✓ Analysis completed successfully
        </div>
      )}

      {/* Version Selector - fixed to properly navigate and fetch child spec data */}
      {apiData.apiVersions && apiData.apiVersions.length > 0 && (
        <div className="version-selector">
          {apiData.apiVersions.map((version: any) => (
            <button
              key={version.id}
              className={`version-chip ${version.id === apiId ? 'active' : ''}`}
              onClick={() => navigate(`/spec-analysis/${version.id}`)}
            >
              {version.versionId}
            </button>
          ))}
        </div>
      )}

      {/* Two-Panel Layout */}
      {analysis && apiData.specText ? (
        <div className="analysis-layout">
          {/* Left Panel: Spec Viewer (Inline Renderer) */}
          <div className="spec-viewer-panel">
            <div className="spec-viewer-header">
              <span className="spec-filename">{apiData.id}.yaml</span>  
            </div>
            <div className="spec-viewer-content">
              {lines.map((line: string, idx: number) => {
                const lineNumber = idx + 1;
                const violations = violationsByLine.get(lineNumber) || [];
                const hasError = violations.some((v: any) => v.severity === 'error');
                const hasWarning = violations.some((v: any) => v.severity === 'warn');
                const isSelected = selectedLine === lineNumber;

                const lineClass = [
                  'spec-line',
                  hasError && 'line-error',
                  hasWarning && !hasError && 'line-warning',
                  isSelected && 'line-selected',
                ].filter(Boolean).join(' ');

                return (
                  <div
                    key={idx}
                    ref={el => {
                      if (el) lineRefs.current.set(lineNumber, el);
                      else lineRefs.current.delete(lineNumber);
                    }}
                    className={lineClass}
                    onClick={() => setSelectedLine(lineNumber)}
                  >
                    <div className="line-number">
                      {(hasError || hasWarning) && (
                        <span className={`line-icon ${hasError ? 'error' : 'warning'}`}>
                          {hasError ? '●' : '▲'}
                        </span>
                      )}
                      {lineNumber}
                    </div>
                    <div className="line-code">
                      <code>{line}</code>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Panel: Findings */}
          <div className="findings-panel">
            <div className="findings-header">
              <h2>Analysis Findings</h2>
              <span className="findings-count">{analysis.findings.length} Issues</span>
            </div>

            {analysis.findings.length === 0 ? (
              <div className="no-findings">
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>✓</div>
                <p>No issues found - specification is fully compliant!</p>
              </div>
            ) : (
              <ul className="findings-list">
                {analysis.findings.map((finding: any, index: number) => (
                  <li key={index} className={`finding-item finding-${finding.severity}`}>
                    <div className="finding-icon-container">
                      {finding.severity === 'error' ? '✕' : '⚠'}
                    </div>
                    <div className="finding-content">
                      <div className="finding-header">
                        <h3>{finding.code.replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}</h3>
                        {finding.line && (
                          <button className="line-link" onClick={() => jumpToLine(finding.line)}>
                            Line {finding.line}
                          </button>
                        )}
                      </div>
                      <p className="finding-message">{finding.message}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : (
        <div className="no-analysis">
          <div style={{ fontSize: '48px', marginBottom: '16px', color: '#0277bd' }}>ℹ</div>
          <p>{apiData.isParentAggregate ? 'Please select a specific version to view its analysis.' : 'No analysis available for this API.'}</p>
          {!apiData.isParentAggregate && (
            <button
              className="run-analysis-button"
              onClick={handleRunAnalysis}
              disabled={!isAdmin}
              title={!isAdmin ? 'Only admins can run analysis' : 'Run Analysis Now'}
            >
              Run Analysis Now
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SpecAnalysisPage;